import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/Form/Input';
import { EntityEditLayout, ErrorBanner, LoadingSpinner } from '../../components';
import { useToast } from '../../components/Toast';
import { getPasswordValidationError, PASSWORD_REQUIREMENTS_MESSAGE } from '@ministry-of-wine/shared';
import { useProfile, useUpdateProfile } from '../../hooks/useProfile';
import { authService } from '../../services/auth.service';
import type { ProfileResponse } from '../../services/profile.service';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const ProfilePage: React.FC = () => {
  const { renderState, refetch } = useProfile();
  const updateProfile = useUpdateProfile();

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const toast = useToast();
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileFormData, string>>>({});

  // Track the loaded profile for change detection
  const profileRef = useRef<ProfileResponse | null>(null);

  // Populate form when profile data loads
  useEffect(() => {
    if (renderState.status === 'success' && renderState.data) {
      const profile = renderState.data;
      profileRef.current = profile;
      setFormData({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        email: profile.email ?? '',
        phone: profile.phone ?? '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [renderState.status, renderState.data]);

  if (renderState.status === 'loading') {
    return <LoadingSpinner message="Loading profile..." />;
  }

  if (renderState.status === 'error') {
    return <ErrorBanner error={renderState.error} onRetry={() => refetch()} />;
  }

  const profile = profileRef.current ?? renderState.data;
  if (!profile) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  const originalEmail = profile.email;
  const emailChanged = formData.email !== originalEmail;
  const showCurrentPassword = emailChanged || formData.newPassword.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name as keyof ProfileFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Real-time validation for password fields
    if (name === 'newPassword') {
      const passwordValidationError = value ? getPasswordValidationError(value) : undefined;
      setErrors(prev => ({
        ...prev,
        newPassword: passwordValidationError || undefined,
        confirmPassword:
          formData.confirmPassword && formData.confirmPassword !== value
            ? 'Passwords do not match'
            : undefined,
      }));
      return;
    }

    if (name === 'confirmPassword') {
      setErrors(prev => ({
        ...prev,
        confirmPassword: value && value !== formData.newPassword ? 'Passwords do not match' : undefined,
      }));
      return;
    }

    // Real-time email validation
    if (name === 'email') {
      if (!value.trim()) {
        setErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProfileFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.newPassword) {
      const passwordValidationError = getPasswordValidationError(formData.newPassword);
      if (passwordValidationError) {
        newErrors.newPassword = passwordValidationError;
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (showCurrentPassword && !formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Build payload with only changed fields
    const payload: Record<string, string> = {};

    if (formData.firstName !== profile.firstName) payload.firstName = formData.firstName;
    if (formData.lastName !== profile.lastName) payload.lastName = formData.lastName;
    if (formData.email !== profile.email) payload.email = formData.email;
    if (formData.phone !== (profile.phone ?? '')) payload.phone = formData.phone;
    if (formData.newPassword) payload.newPassword = formData.newPassword;
    if (showCurrentPassword) payload.currentPassword = formData.currentPassword;

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      return;
    }

    try {
      const response = await updateProfile.mutateAsync(payload);

      // Update localStorage with new user data
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        const updatedStoredUser = {
          ...storedUser,
          first_name: response.firstName,
          last_name: response.lastName,
          email: response.email,
          name: `${response.firstName} ${response.lastName}`.trim(),
        };
        localStorage.setItem('user', JSON.stringify(updatedStoredUser));
      }

      // Update the ref so change detection works against the new values
      profileRef.current = response;

      // Clear password fields on success
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setErrors({});
      toast.success('Profile updated successfully');
    } catch (err: any) {
      const details = err?.details as { field: string; message: string }[] | undefined;

      if (details && Array.isArray(details)) {
        const fieldErrors: Partial<Record<keyof ProfileFormData, string>> = {};
        details.forEach((d) => {
          if (d.field === 'currentPassword' || d.field === 'email') {
            fieldErrors[d.field as keyof ProfileFormData] = d.message;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...fieldErrors }));
          return;
        }
      }

      // Generic error via toast
      toast.error(err?.message || 'Failed to update profile. Please try again.');
    }
  };

  const formatRole = (role: string): string => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <EntityEditLayout
      backTo="/inventory/bulk-wine"
      backLabel="Back to Dashboard"
      title="My Profile"
    >
      <form onSubmit={handleSubmit}>
        {/* Role & Supplier Info */}
        <div className="mb-6">
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
          }}>
            <p style={{
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#667085',
            }}>
              <span style={{ fontWeight: 700, color: '#344054' }}>Role:</span>{' '}
              {formatRole(profile.role)}
            </p>
            {profile.supplierName && (
              <p style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                color: '#667085',
              }}>
                <span style={{ fontWeight: 700, color: '#344054' }}>Supplier:</span>{' '}
                {profile.supplierName}
              </p>
            )}
          </div>
        </div>

        {/* Personal Information Card */}
        <div className="mb-6">
          <div style={{
            display: 'flex',
            width: '733px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            padding: '24px'
          }}>
            <h2 style={{
              fontFamily: 'Oxygen',
              fontSize: '18px',
              fontWeight: 700,
              color: '#101828',
              marginBottom: '24px'
            }}>
              Personal Information
            </h2>

            <div className="space-y-6 w-full">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  First Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  error={errors.firstName}
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Last Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  error={errors.lastName}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Email <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  error={errors.email}
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Phone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  error={errors.phone}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="mb-6">
          <div style={{
            display: 'flex',
            width: '733px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            padding: '24px'
          }}>
            <h2 style={{
              fontFamily: 'Oxygen',
              fontSize: '18px',
              fontWeight: 700,
              color: '#101828',
              marginBottom: '24px'
            }}>
              Change Password
            </h2>

            <div className="space-y-6 w-full">
              {/* Current Password */}
              <div>
                <label htmlFor="currentPassword" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Current Password {showCurrentPassword && <span style={{ color: '#DC2626' }}>*</span>}
                </label>
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter current password"
                  error={errors.currentPassword}
                  hint="Required when changing email or password"
                />
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  New Password
                </label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  error={errors.newPassword}
                  hint={PASSWORD_REQUIREMENTS_MESSAGE}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Confirm New Password
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  error={errors.confirmPassword}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 mt-8 max-w-[733px]">
          <Button type="submit" variant="primary" disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </EntityEditLayout>
  );
};

export default ProfilePage;
